import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTooth, FaPhone, FaBars, FaTimes, FaUserMd, FaCalendarCheck } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      const sections = ['hero', 'about', 'services', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveLink(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setIsOpen(false);
      setActiveLink(sectionId);
    }
  };

  const navLinks = [
    { id: 'hero', label: 'Home', icon: <FaTooth /> },
    { id: 'about', label: 'About', icon: <FaUserMd /> },
    { id: 'services', label: 'Services', icon: <FaTooth /> },
    { id: 'contact', label: 'Contact', icon: <FaCalendarCheck /> }
  ];

const styles = {
    navbar: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 1000,
        padding: scrolled ? '15px 0' : '25px 0',
        background: scrolled 
            ? 'rgba(10, 15, 28, 0.98)' 
            : 'linear-gradient(to bottom, rgba(10, 15, 28, 0.95), rgba(10, 15, 28, 0.8))',
        backdropFilter: scrolled ? 'blur(10px)' : 'blur(5px)',
        borderBottom: scrolled ? '1px solid rgba(200, 169, 126, 0.2)' : 'none',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: scrolled ? 'var(--shadow-md)' : 'none'
    },
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: window.innerWidth < 768 ? '0 20px' : '0 40px'
    },
    logo: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer'
    },
    logoIcon: {
        fontSize: window.innerWidth < 768 ? '32px' : '40px',
        color: 'var(--primary)',
        filter: 'drop-shadow(0 0 20px rgba(200, 169, 126, 0.5))',
        animation: 'float 3s ease-in-out infinite'
    },
    logoText: {
        fontSize: window.innerWidth < 768 ? '22px' : '28px',
        fontFamily: 'Playfair Display, serif',
        fontWeight: 800,
        background: 'var(--gradient-gold)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        letterSpacing: '1px',
        textShadow: '0 2px 10px rgba(200, 169, 126, 0.3)'
    },
    logoSpan: {
        color: '#ffffff',
        background: 'none',
        WebkitTextFillColor: '#ffffff',
        marginLeft: '5px',
        textShadow: '0 2px 10px rgba(255, 255, 255, 0.3)'
    },
    navMenu: {
        display: 'flex',
        gap: window.innerWidth < 768 ? '20px' : '40px',
        alignItems: 'center'
    },
    navLink: {
        color: scrolled ? '#ffffff' : '#ffffff',
        textDecoration: 'none',
        fontSize: window.innerWidth < 768 ? '14px' : '16px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
        cursor: 'pointer',
        transition: 'all 0.3s',
        textShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
        letterSpacing: '0.5px'
    },
    activeLink: {
        color: 'var(--primary)',
        textShadow: '0 0 10px rgba(200, 169, 126, 0.5)'
    },
    linkIndicator: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '3px',
        background: 'var(--gradient-gold)',
        borderRadius: '3px',
        boxShadow: '0 0 10px rgba(200, 169, 126, 0.5)'
    },
    phoneBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'var(--gradient-gold)',
        color: '#0A0F1C',
        padding: window.innerWidth < 768 ? '10px 15px' : '12px 25px',
        borderRadius: '50px',
        textDecoration: 'none',
        fontWeight: 700,
        fontSize: window.innerWidth < 768 ? '14px' : '16px',
        border: 'none',
        cursor: 'pointer',
        boxShadow: 'var(--shadow-gold)',
        transition: 'all 0.3s',
        textShadow: 'none'
    },
    menuBtn: {
        display: window.innerWidth <= 968 ? 'block' : 'none',
        background: 'transparent',
        border: 'none',
        color: 'var(--primary)',
        fontSize: '28px',
        cursor: 'pointer',
        zIndex: 1001,
        textShadow: '0 0 10px rgba(200, 169, 126, 0.5)'
    },
    mobileMenu: {
        position: 'fixed',
        top: 0,
        right: 0,
        width: '100%',
        height: '100vh',
        background: 'rgba(10, 15, 28, 0.98)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: window.innerWidth < 480 ? '20px' : '30px',
        zIndex: 1000,
        padding: '20px'
    },
    mobileLink: {
        color: '#ffffff',
        fontSize: window.innerWidth < 480 ? '22px' : '28px',
        fontWeight: 600,
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: window.innerWidth < 480 ? '12px 30px' : '15px 40px',
        borderRadius: '60px',
        transition: 'all 0.3s',
        cursor: 'pointer',
        textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
    }
};

  return (
    <>
      <motion.nav 
        style={styles.navbar}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, type: 'spring', stiffness: 100 }}
      >
        <div style={styles.container}>
          <motion.div 
            style={styles.logo}
            onClick={() => scrollToSection('hero')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaTooth style={styles.logoIcon} />
            <h1 style={styles.logoText}>
              Maxfax<span style={styles.logoSpan}>Dental</span>
            </h1>
          </motion.div>

          {/* Desktop Menu */}
          <div style={{ ...styles.navMenu, display: window.innerWidth > 968 ? 'flex' : 'none' }}>
            {navLinks.map((link) => (
              <div key={link.id} style={{ position: 'relative' }}>
                <motion.a
                  style={{
                    ...styles.navLink,
                    ...(activeLink === link.id ? styles.activeLink : {})
                  }}
                  onClick={() => scrollToSection(link.id)}
                  whileHover={{ y: -2, color: 'var(--primary)' }}
                  whileTap={{ y: 0 }}
                >
                  {link.icon}
                  {link.label}
                </motion.a>
                {activeLink === link.id && (
                  <motion.div 
                    style={styles.linkIndicator}
                    layoutId="activeLink"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <motion.a 
              href="tel:03014484633" 
              style={styles.phoneBtn}
              whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(200, 169, 126, 0.6)' }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPhone />
              <span>0301 4484633</span>
            </motion.a>

            {/* Mobile Menu Button */}
            <motion.button 
              style={styles.menuBtn}
              onClick={() => setIsOpen(!isOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            style={styles.mobileMenu}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
          >
            {navLinks.map((link, index) => (
              <motion.a
                key={link.id}
                style={styles.mobileLink}
                onClick={() => scrollToSection(link.id)}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.1,
                  color: 'var(--primary)',
                  background: 'rgba(200, 169, 126, 0.1)'
                }}
              >
                {link.icon}
                {link.label}
              </motion.a>
            ))}
            
            <motion.a 
              href="tel:03014484633"
              style={{
                ...styles.phoneBtn,
                marginTop: '30px',
                fontSize: '24px',
                padding: '20px 50px'
              }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaPhone /> Call Now
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;